<template>
  <div>
    <div class="p-personGenerateText-resultBox p20 sm-p10">
      <div v-if="memo.genstatus !== 'generated' && memo.genstatus !== 'failed'">
        <div class="u-skelton-line"></div>
        <div class="u-skelton-line mt10"></div>
        <div class="u-skelton-line mt10" style="width: 80%;"></div>
      </div>
      <!-- 生成結果 -->
      <div v-if="memo.genstatus === 'generated'">
        <div class="fw-b">
          <p style="white-space: pre-wrap;">{{ this.memo.text }}</p>
        </div>
      </div>
      <div v-if="memo.genstatus === 'failed'">
        <div class="fw-b">
          <p>生成に失敗しました。<br>ポイントは消費されません。</p>
        </div>
      </div>
    </div>

    <p class="c-red fw-b ta-c fz105 mt20" v-if="memo.genstatus === 'generated'">
      <span v-if="isSaveProfile">生成したテキストは、<br>メモとプロフィールに追加保存されました！<br>プロフィールは<a href="/person/my_page/introduction" class="under-line hov">こちら</a></span>
      <span v-else>生成したテキストは、<br>メモに追加保存されました！</span>
    </p>

    <div class="mt20 d-f gap10" v-if="memo.genstatus === 'generated'">
      <a :href="propRegenPath" class="d-b bdrs10 f1 hov" :class="`bgc-${colors[gentype]}`" style="padding: 2px 2px 6px 2px; display: grid;">
        <div class="bgc-white p10 bdrs8 h-max d-f jc-c fxd-c">
          <figure class="ta-c"><img :src="`/img/symbol/ai-binder-${gentype}.svg`" width="32"></figure>
          <p class="fw-b ta-c fz115 lh120 mt5">内容や文字数を<br>変更して再生成</p>
        </div>
      </a>
      <a :href="propMemoPath" class="bgc-lemonyellow bdrs10 f1 hov" style="padding: 2px 2px 6px 2px; display: grid;">
        <div class="bgc-white p10 bdrs8 h-max d-f jc-c fxd-c">
          <figure class="ta-c"><img src="/img/u-memo/book-and-pen.svg" width="26"></figure>
          <p class="fw-b ta-c fz115 lh120 mt5">メモを見る</p>
        </div>
      </a>
    </div>
  </div>
</template>

<script>
  import { popupSuccess, popupError } from '@/packs/person/popup';

  const INITIAL_POLL_MS = 3000;
  const MAX_POLL_MS = 30000;
  const POLL_TIMEOUT_MS = 10 * 60 * 1000;

  export default {
    props: {
      propGentype: String,
      propMemo: Object,
      propGeneratePath: String,
      propStatusPath: String,
      propRegenPath: String,
      propMemoPath: String,
    },
    data() {
      return {
        gentype: '',
        memo: {},
        isSaveProfile: false,
        pollTimer: null,
        pollInterval: INITIAL_POLL_MS,
        pollStartedAt: null,
        colors: {
          selfpr: 'key',
          reason: 'red',
          point: 'blue',
          gakuchika: 'purple',
          seminar: 'seminar',
          'career-history': 'limegreen',
        },
      }
    },
    created() {
      this.gentype = this.propGentype;
      this.memo = this.propMemo;
      this.boot();
    },
    beforeDestroy() {
      this.clearPoll();
    },
    methods: {
      async boot() {
        if (this.memo.genstatus === 'generated') {
          popupSuccess('生成が完了しています!');
          return;
        }

        if (this.memo.genstatus === 'pending') {
          await this.postGenerateRequest();
        }

        if (this.memo.genstatus === 'sent') {
          this.startPolling();
        }
      },

      async postGenerateRequest() {
        const res = await fetch(this.propGeneratePath, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ genid: this.memo.genid }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data.memo) {
            this.memo = data.memo;
          }
          popupError('生成に失敗しました。ポイントは消費されません。');
          return;
        }

        const data = await res.json();
        this.memo = data.memo;
        this.isSaveProfile = !!data.is_save_profile;

        if (this.memo.genstatus === 'generated') {
          popupSuccess('生成しました!');
        }
      },

      startPolling() {
        if (this.pollTimer) return;
        this.pollInterval = INITIAL_POLL_MS;
        this.pollStartedAt = Date.now();
        this.schedulePoll();
        this.fetchStatus();
      },

      schedulePoll() {
        this.pollTimer = setTimeout(() => {
          if (Date.now() - this.pollStartedAt >= POLL_TIMEOUT_MS) {
            this.clearPoll();
            popupError('生成に時間がかかっています。ページを再読み込みしてください。');
            return;
          }
          this.fetchStatus();
          this.pollInterval = Math.min(this.pollInterval * 2, MAX_POLL_MS);
          this.schedulePoll();
        }, this.pollInterval);
      },

      clearPoll() {
        if (this.pollTimer) {
          clearTimeout(this.pollTimer);
          this.pollTimer = null;
        }
      },

      async fetchStatus() {
        try {
          const res = await fetch(this.propStatusPath, {
            headers: { Accept: 'application/json' },
          });

          if (!res.ok) {
            this.clearPoll();
            popupError('生成に失敗したか、データが見つかりません。');
            return;
          }

          const data = await res.json();
          const prev = this.memo.genstatus;
          this.memo = data.memo;
          this.isSaveProfile = !!data.is_save_profile;

          if (this.memo.genstatus === 'generated') {
            this.clearPoll();
            if (prev !== 'generated') {
              popupSuccess('生成しました!');
            }
          }

          if (this.memo.genstatus === 'failed') {
            this.clearPoll();
            popupError('生成に失敗しました。ポイントは消費されません。');
          }
        } catch (_e) {
          this.clearPoll();
          popupError('状態の取得に失敗しました。');
        }
      },
    }
  }
</script>
